import { Controller } from "@hotwired/stimulus";

/**
 * Stimulus controller for managing question template forms.
 * Handles dynamic answer options and data fields based on question type.
 *
 * @class QuestionTemplateFormController
 * @extends Controller
 */
export default class extends Controller {
  static targets = [
    "answerOptionsContainer",
    "answerOptionsTable",
    "dataFieldsContainer",
    "dataFieldsTable",
    "questionType",
    "answerOptionTemplate",
    "dataFieldTemplate"
  ];

  /**
   * Called when the controller is connected to the DOM.
   * Initializes the form based on current question type.
   */
  connect() {
    this.updateFormSections();
  }

  /**
   * Updates form sections visibility based on selected question type.
   * Question types:
   * - "1": Single select (answer options only)
   * - "2", "3", "6", "7": Grid/multi questions (both answer options and data fields)
   *   NOTE: "7" (Heatmap) is deprecated but retained for backward compatibility
   * - Others: Neither section enabled
   */
  updateFormSections() {
    if (!this.hasQuestionTypeTarget) return;

    const qtype = this.questionTypeTarget.value;
    const enableAnswerOptions = ["1", "2", "3", "6", "7"].includes(qtype);
    const enableDataFields = ["2", "3", "6", "7"].includes(qtype);

    this.toggleAnswerOptions(enableAnswerOptions);
    this.toggleDataFields(enableDataFields);
  }

  /**
   * Toggles answer options section visibility.
   * @param {boolean} enable - Whether to enable the section
   */
  toggleAnswerOptions(enable) {
    if (!this.hasAnswerOptionsContainerTarget) return;

    const container = this.answerOptionsContainerTarget;
    const body = container.querySelector("#answer-options-body");

    if (enable) {
      // Show table
      if (this.hasAnswerOptionsTableTarget) {
        this.answerOptionsTableTarget.style.display = "";
      }
      // Create add button using safe DOM methods
      if (body) {
        this.renderAddButton(body, "Add Answer Option", "question-template-form#addAnswerOption");
      }
    } else {
      // Show disabled message
      if (this.hasAnswerOptionsTableTarget) {
        this.answerOptionsTableTarget.style.display = "none";
      }
      if (body) {
        this.renderDisabledMessage(body, "Options only show up for single and grid questions.");
      }
    }
  }

  /**
   * Toggles data fields section visibility.
   * @param {boolean} enable - Whether to enable the section
   */
  toggleDataFields(enable) {
    if (!this.hasDataFieldsContainerTarget) return;

    const container = this.dataFieldsContainerTarget;
    const body = container.querySelector("#data-fields-body");

    if (enable) {
      // Show table
      if (this.hasDataFieldsTableTarget) {
        this.dataFieldsTableTarget.style.display = "";
      }
      // Create add button using safe DOM methods
      if (body) {
        this.renderAddButton(body, "Add Data Field Row", "question-template-form#addDataField");
      }
    } else {
      // Show disabled message
      if (this.hasDataFieldsTableTarget) {
        this.dataFieldsTableTarget.style.display = "none";
      }
      if (body) {
        this.renderDisabledMessage(body, "Only Grid and Multi questions use Data Fields");
      }
    }
  }

  /**
   * Renders an add button using safe DOM methods.
   * @param {HTMLElement} container - Container element
   * @param {string} buttonText - Button text
   * @param {string} action - Stimulus action
   */
  renderAddButton(container, buttonText, action) {
    // Clear existing content
    while (container.firstChild) {
      container.removeChild(container.firstChild);
    }

    const col = document.createElement("div");
    col.className = "col d-flex justify-content-between";

    const button = document.createElement("button");
    button.type = "button";
    button.className = "btn btn-live px-5";
    button.setAttribute("data-action", `click->${action}`);
    button.textContent = buttonText;

    col.appendChild(button);
    container.appendChild(col);
  }

  /**
   * Renders a disabled message using safe DOM methods.
   * @param {HTMLElement} container - Container element
   * @param {string} message - Message text
   */
  renderDisabledMessage(container, message) {
    // Clear existing content
    while (container.firstChild) {
      container.removeChild(container.firstChild);
    }

    const p = document.createElement("p");
    p.textContent = message;
    container.appendChild(p);
  }

  /**
   * Adds a new answer option row from the template.
   * @param {Event} event
   */
  addAnswerOption(event) {
    event.preventDefault();

    if (!this.hasAnswerOptionTemplateTarget || !this.hasAnswerOptionsTableTarget) return;

    const tbody = this.answerOptionsTableTarget.querySelector("tbody");
    if (!tbody) return;

    const count = this.getRowCount(tbody);
    const row = this.createRowFromTemplate(
      this.answerOptionTemplateTarget,
      "answer_ops",
      count
    );

    tbody.appendChild(row);
  }

  /**
   * Removes an answer option row.
   * Prevents deletion if only one row remains.
   * @param {Event} event
   */
  removeAnswerOption(event) {
    event.preventDefault();

    if (!this.hasAnswerOptionsTableTarget) return;

    const tbody = this.answerOptionsTableTarget.querySelector("tbody");
    if (!tbody) return;

    const rowCount = this.getRowCount(tbody);
    if (rowCount <= 1) {
      return; // Prevent deletion of last row
    }

    const row = event.currentTarget.closest("tr");
    if (row) {
      row.remove();
    }
  }

  /**
   * Adds a new data field row from the template.
   * @param {Event} event
   */
  addDataField(event) {
    event.preventDefault();

    if (!this.hasDataFieldTemplateTarget || !this.hasDataFieldsTableTarget) return;

    const tbody = this.dataFieldsTableTarget.querySelector("tbody");
    if (!tbody) return;

    const count = this.getRowCount(tbody);
    const row = this.createRowFromTemplate(
      this.dataFieldTemplateTarget,
      "data_fields",
      count
    );

    tbody.appendChild(row);
  }

  /**
   * Removes a data field row.
   * Prevents deletion if only one row remains.
   * @param {Event} event
   */
  removeDataField(event) {
    event.preventDefault();

    if (!this.hasDataFieldsTableTarget) return;

    const tbody = this.dataFieldsTableTarget.querySelector("tbody");
    if (!tbody) return;

    const rowCount = this.getRowCount(tbody);
    if (rowCount <= 1) {
      return; // Prevent deletion of last row
    }

    const row = event.currentTarget.closest("tr");
    if (row) {
      row.remove();
    }
  }

  /**
   * Creates a new row from a template element.
   * Uses safe DOM manipulation with createElement and textContent.
   * @param {HTMLTemplateElement} template - Template element to clone
   * @param {string} section - Section name (answer_ops or data_fields)
   * @param {number} count - Row index
   * @returns {HTMLElement} The created row
   */
  createRowFromTemplate(template, section, count) {
    const content = template.content.cloneNode(true);
    const row = content.querySelector("tr");

    if (row) {
      // Update row ID
      row.id = `${section}_${count}`;

      // Update all input names and IDs with the count
      const inputs = row.querySelectorAll("input");
      inputs.forEach(input => {
        if (input.name) {
          input.name = input.name.replace(/INDEX/g, count);
        }
        if (input.id) {
          input.id = input.id.replace(/INDEX/g, count);
        }
      });

      // Update precode input default value
      const precodeInput = row.querySelector('input[name*="[precode]"]');
      if (precodeInput && !precodeInput.value) {
        precodeInput.value = String(count + 1);
      }
    }

    return row;
  }

  /**
   * Gets the current row count in a tbody element.
   * @param {HTMLElement} tbody
   * @returns {number}
   */
  getRowCount(tbody) {
    return tbody.querySelectorAll("tr").length;
  }

  /**
   * Opens the modal for inserting a template into a survey.
   * @param {Event} event
   */
  openInsertModal(event) {
    event.preventDefault();

    const templateId = event.params.templateId;
    if (!templateId) return;

    const form = document.getElementById("questionTemplateModalForm");
    if (form) {
      form.action = `/question_templates/${templateId}/add_to_survey`;
    }

    // Trigger Bootstrap modal (using jQuery since Bootstrap 4 is available)
    const modal = document.getElementById("questionTemplateModal");
    if (modal && typeof $ !== "undefined") {
      $(modal).modal("show");
    }
  }
}
